import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Sparkles, BookOpen, FileText, Music, Play, Users, 
  Crown, Settings, LogOut, Menu, X, ChevronRight, Lock, Trophy, Video, Calendar, Mic, User, ExternalLink, Shield, Zap, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin emails list (should match backend)
const ADMIN_EMAILS = ['info@heatherstewart.coach'];

// Sidebar Navigation Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout, isPremium, isVip, isFoundingMember, membershipTier, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (user?.email && ADMIN_EMAILS.includes(user.email)) {
      setIsAdmin(true);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const freeMenuItems = [
    { icon: BookOpen, label: 'Ebook', path: '/dashboard/ebook' },
    { icon: FileText, label: 'Journals', path: '/dashboard/journals' },
    { icon: Music, label: 'Meditations', path: '/dashboard/meditations' },
    { icon: Mic, label: 'Podcast', path: '/dashboard/podcast' },
    { icon: Play, label: 'Gentle Movement Recordings', path: '/dashboard/classes' },
    { icon: Users, label: 'Community', path: '/dashboard/community' },
    { icon: MessageCircle, label: 'Messages', path: '/dashboard/messages' },
    { icon: ExternalLink, label: 'Other Resources', path: '/dashboard/resources' },
  ];

  const comingSoonItems = [
    { icon: Sparkles, label: 'Retreat 2027', path: '/dashboard/retreat', badge: 'Coming Soon' },
  ];

  const foundingMenuItems = [
    { icon: Calendar, label: 'Live Sessions', path: '/dashboard/events', tier: 'founding' },
    { icon: Users, label: 'Founding Community Conversation', path: '/dashboard/founding-community', tier: 'founding' },
    { icon: Play, label: 'Session Replays', path: '/dashboard/session-replays', tier: 'founding' },
  ];

  const vipMenuItems = [
    { icon: MessageCircle, label: 'Coach in Your Pocket', path: '/dashboard/coach-pocket', tier: 'vip' },
    { icon: Users, label: 'VIP Member Conversations', path: '/dashboard/vip-community', tier: 'vip' },
    { icon: ExternalLink, label: 'Other Resources', path: '/dashboard/vip-resources', tier: 'vip' },
  ];

  const isActive = (path) => location.pathname === path;

  const getTierBadge = () => {
    if (membershipTier === 'vip') return { text: 'VIP', color: 'bg-gradient-to-r from-amber-500 to-orange-500' };
    if (membershipTier === 'founding') return { text: 'Founding', color: 'bg-gradient-to-r from-purple-500 to-pink-500' };
    return null;
  };

  const tierBadge = getTierBadge();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-white border-r border-stone-200 z-50
        transform transition-transform duration-300 lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-stone-200">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-syne font-bold text-lg text-stone-900 leading-tight">Heather Stewart</span>
                <span className="text-xs text-stone-500">Transformation Guide</span>
              </div>
            </Link>
            <button 
              className="lg:hidden absolute top-6 right-6 text-stone-500"
              onClick={() => setIsOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User info */}
          <div className="p-6 border-b border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center text-white font-semibold text-lg overflow-hidden">
                {user?.avatar_url ? (
                  <img 
                    src={user.avatar_url.startsWith('/api/uploads/') || user.avatar_url.startsWith('/uploads/')
                      ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` 
                      : user.avatar_url
                    } 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  user?.name?.charAt(0) || user?.first_name?.charAt(0) || 'U'
                )}
              </div>
              <div>
                <p className="font-semibold text-stone-900">
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user?.name || 'User'}
                </p>
                {tierBadge ? (
                  <span className={`${tierBadge.color} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}>
                    {tierBadge.text}
                  </span>
                ) : (
                  <span className="text-sm text-stone-500">Free Member</span>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            {/* 30-Day Alignment Lab */}
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-3">Program</p>
            <ul className="space-y-1 mb-6">
              <li>
                <Link
                  to="/dashboard/alignment-lab"
                  onClick={() => setIsOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                    ${isActive('/dashboard/alignment-lab') 
                      ? 'bg-violet-500/10 text-violet-600 font-medium' 
                      : 'text-stone-600 hover:bg-stone-100'}
                  `}
                  data-testid="nav-worth-lab"
                >
                  <Zap className="w-5 h-5" />
                  30-Day Alignment Lab
                </Link>
              </li>
            </ul>

            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-3">Free Content</p>
            <ul className="space-y-1 mb-6">
              {freeMenuItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                      ${isActive(item.path) 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-stone-600 hover:bg-stone-100'}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-3">Coming Soon</p>
            <ul className="space-y-1 mb-6">
              {comingSoonItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`
                      flex items-center justify-between px-3 py-3 rounded-xl transition-all
                      ${isActive(item.path) 
                        ? 'bg-accent/10 text-accent font-medium' 
                        : 'text-stone-600 hover:bg-stone-100'}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </div>
                    <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-3">Founding Member</p>
            <ul className="space-y-1 mb-6">
              {foundingMenuItems.map((item) => (
                <li key={item.path}>
                  {isFoundingMember ? (
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`
                        flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                        ${isActive(item.path) 
                          ? 'bg-primary/10 text-primary font-medium' 
                          : 'text-stone-600 hover:bg-stone-100'}
                      `}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between px-3 py-3 rounded-xl text-stone-400">
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </div>
                      <Lock className="w-4 h-4" />
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-3">VIP Member</p>
            <ul className="space-y-1">
              {vipMenuItems.map((item) => (
                <li key={item.path}>
                  {isVip ? (
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`
                        flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                        ${isActive(item.path) 
                          ? 'bg-amber-500/10 text-amber-600 font-medium' 
                          : 'text-stone-600 hover:bg-stone-100'}
                      `}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between px-3 py-3 rounded-xl text-stone-400">
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </div>
                      <Lock className="w-4 h-4" />
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {!isFoundingMember && (
              <div className="mt-6 p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl">
                <p className="font-semibold text-stone-900 mb-2">Become a Founding Member</p>
                <p className="text-sm text-stone-600 mb-4">Get workshops, community & support</p>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="w-full btn-cosmic text-sm py-3"
                  data-testid="sidebar-upgrade-btn"
                >
                  Upgrade - $11/mo
                </Button>
              </div>
            )}

            {isFoundingMember && !isVip && (
              <div className="mt-6 p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl">
                <p className="font-semibold text-stone-900 mb-2">Coach in Your Pocket</p>
                <p className="text-sm text-stone-600 mb-4">Direct WhatsApp access to Heather</p>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm py-3 rounded-full"
                  data-testid="sidebar-vip-upgrade-btn"
                >
                  Upgrade - $75/mo
                </Button>
              </div>
            )}
          </nav>

          {/* Bottom actions */}
          <div className="p-4 border-t border-stone-200">
            {isAdmin && (
              <Link 
                to="/dashboard/admin"
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-primary hover:bg-primary/10 transition-colors w-full mb-2 font-medium"
                data-testid="nav-admin"
              >
                <Shield className="w-5 h-5" />
                Admin Dashboard
              </Link>
            )}
            <Link 
              to="/dashboard/profile"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-stone-600 hover:bg-stone-100 transition-colors w-full mb-2"
              data-testid="nav-profile"
            >
              <User className="w-5 h-5" />
              My Profile
            </Link>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-stone-600 hover:bg-stone-100 transition-colors w-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Layout Component
export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Main content */}
      <div className="lg:ml-72 min-h-screen overflow-y-auto">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-stone-200 px-4 py-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6 text-stone-700" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-syne font-bold text-stone-900">Heather Stewart</span>
          </div>
          <NotificationBell />
        </header>

        {/* Desktop notification bell - fixed position */}
        <div className="hidden lg:block fixed top-6 right-8 z-40">
          <NotificationBell />
        </div>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Dashboard Home
export const DashboardHome = () => {
  const { user, isPremium, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [ebookProgress, setEbookProgress] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);

  useEffect(() => {
    fetchEbookProgress();
  }, []);

  const fetchEbookProgress = async () => {
    try {
      const [progressRes, chaptersRes] = await Promise.all([
        axios.get(`${API_URL}/ebook/progress`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/ebook/chapters`, { headers: getAuthHeader() })
      ]);
      setEbookProgress(progressRes.data);
      
      // Find current chapter details
      if (progressRes.data.current_chapter && chaptersRes.data.length > 0) {
        const chapter = chaptersRes.data.find(ch => ch.id === progressRes.data.current_chapter);
        setCurrentChapter(chapter || chaptersRes.data[0]);
      } else if (chaptersRes.data.length > 0) {
        setCurrentChapter(chaptersRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch ebook progress:', error);
    }
  };

  const quickLinks = [
    { icon: Zap, title: '30-Day Alignment Lab', desc: 'Your transformation program', path: '/dashboard/alignment-lab', color: 'violet-500' },
    { icon: BookOpen, title: 'Ebook', desc: 'Continue your journey', path: '/dashboard/ebook', color: 'primary' },
    { icon: FileText, title: 'Journals', desc: 'Guided journaling', path: '/dashboard/journals', color: 'amber-500' },
    { icon: Music, title: 'Meditations', desc: 'Find your peace', path: '/dashboard/meditations', color: 'secondary' },
    { icon: Mic, title: 'Podcast', desc: 'Listen & learn', path: '/dashboard/podcast', color: 'rose-500' },
    { icon: Play, title: 'Gentle Movement', desc: 'Movement recordings', path: '/dashboard/classes', color: 'accent' },
    { icon: Users, title: 'Community', desc: 'Join discussions', path: '/dashboard/community', color: 'primary' },
    { icon: MessageCircle, title: 'Messages', desc: 'Private conversations', path: '/dashboard/messages', color: 'fuchsia-500' },
    { icon: ExternalLink, title: 'Other Resources', desc: 'Coaching & more', path: '/dashboard/resources', color: 'teal-500' },
    { icon: Sparkles, title: 'Retreat 2027', desc: 'Coming soon', path: '/dashboard/retreat', color: 'orange-500' },
  ];

  const completionPercentage = ebookProgress?.completion_percentage || 0;

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Welcome */}
      <div className="card-cosmic p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-accent/10 blur-3xl" />
        <div className="relative z-10">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-lg text-stone-600 max-w-xl">
            You don't have to walk this path alone. Let's continue your transformation journey together.
          </p>
        </div>
      </div>

      {/* Continue Where You Left Off */}
      {currentChapter && completionPercentage < 100 && (
        <div className="card-cosmic p-6 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="text-sm text-primary font-medium mb-1">Continue where you left off</p>
                <h3 className="font-bold text-stone-900">{currentChapter.title}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-32 h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-stone-500">{completionPercentage}% complete</span>
                </div>
              </div>
            </div>
            <Button
              onClick={() => navigate('/dashboard/ebook')}
              className="btn-cosmic whitespace-nowrap"
              data-testid="continue-reading-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Continue Reading
            </Button>
          </div>
        </div>
      )}

      {/* Ebook Completed Celebration */}
      {completionPercentage === 100 && (
        <div className="card-cosmic p-6 border-2 border-secondary/30 bg-gradient-to-r from-secondary/10 to-accent/10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-7 h-7 text-secondary" />
            </div>
            <div>
              <h3 className="font-bold text-stone-900">Congratulations! You've completed the ebook!</h3>
              <p className="text-stone-600">Your transformation journey has officially begun. Keep exploring the other resources!</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div>
        <h2 className="text-xl font-bold text-stone-900 mb-4">Quick Access</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="card-cosmic p-6 text-left card-hover group"
              data-testid={`quick-link-${item.title.toLowerCase().replace(' ', '-')}`}
            >
              <div className={`w-12 h-12 rounded-xl bg-${item.color}/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <item.icon className={`w-6 h-6 text-${item.color}`} />
              </div>
              <h3 className="font-semibold text-stone-900 mb-1">{item.title}</h3>
              <p className="text-sm text-stone-500">{item.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Premium CTA */}
      {!isPremium && (
        <div className="card-cosmic p-8 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="badge-premium inline-flex items-center gap-1 mb-3">
                <Crown className="w-4 h-4" /> Premium
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-2">Ready to go deeper?</h3>
              <p className="text-stone-600">Get access to live classes, premium content, and exclusive community.</p>
            </div>
            <Button 
              onClick={() => navigate('/pricing')}
              className="btn-cosmic whitespace-nowrap"
              data-testid="dashboard-upgrade-btn"
            >
              Upgrade to Premium
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
